import {
  useAiChatActions,
  useAiChatAttached,
  useAiChatMessage,
} from '@euler/app/flows/ai-suite/chat/AiChatActions';
import {
  CardLoading,
  CardNotice,
  ChatCard,
} from '@euler/app/flows/ai-suite/components/ChatCard';
import { SecondaryButton } from '@euler/app/flows/ai-suite/components/PrimaryButton';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { ApdmUploadButton } from '@euler/app/flows/apdm/imports/ApdmUploadButton';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiAnswerAction } from '@euler/model/bi/answer';
import { BiUploadedDatasetView } from '@euler/model/bi/dataset';
import { BiDatasetChoiceWidgetPayload } from '@euler/model/bi/widgets';
import { Database, List } from 'lucide-react-native';
import { memo, useState } from 'react';
import { View } from 'react-native';
import { useBiTap } from '../bi-visualization/functions/useBiTap';
import { AllDatasets } from './AllDatasets';
import { DatasetChoiceRow } from './DatasetChoiceRow';
import { biDatasetFacts } from './functions/biDatasetFacts';
import { useBiOfferedDatasets } from './functions/useBiOfferedDatasets';

/**
 * Which of the user's data to answer with: the offered datasets, best
 * match first, then **查看全部** and **上传新文件**. A pick continues the
 * conversation with that dataset; it takes a tap only while it is the
 * latest question, once.
 */
export const DatasetChoiceCard = memo(
  ({ payload }: { payload: BiDatasetChoiceWidgetPayload }) => {
    const { rpx } = useResponsiveContext();
    const attached = useAiChatAttached();
    const { latest } = useAiChatMessage();
    const chat = useAiChatActions();
    const { pending, busy, tap } = useBiTap();
    const state = useBiOfferedDatasets(payload.datasetIds);
    const [all, setAll] = useState(false);
    const [chosen, setChosen] = useState<string | null>(null);
    const open = attached && latest && !chosen;
    const disabled = !open || busy || !!pending;
    const offered = state.kind === 'ready' ? state.datasets : [];
    const choose = (dataset: BiUploadedDatasetView) => {
      if (
        tap(
          { action: BiAnswerAction.SelectDataset, datasetId: dataset.id },
          dataset.name,
        )
      )
        setChosen(`「${dataset.name}」`);
    };
    return (
      <ChatCard
        icon={Database}
        color={aiColors.amber}
        title="选择数据"
        subtitle={offered.length ? `${offered.length} 份数据对得上` : null}
      >
        {chosen ? (
          <CardNotice text={`已选择${chosen}`} />
        ) : (
          <>
            {state.kind === 'loading' ? (
              <CardLoading label="正在读取你的数据…" />
            ) : state.kind === 'failed' ? (
              <CardNotice tone="danger" text="数据列表没有加载出来。" />
            ) : offered.length ? (
              <View style={{ gap: rpx(8) }}>
                {offered.map((dataset, index) => (
                  <DatasetChoiceRow
                    key={dataset.id}
                    name={dataset.name}
                    facts={biDatasetFacts(dataset)}
                    hint={
                      index === 0 && offered.length > 1 ? '最匹配' : undefined
                    }
                    disabled={disabled}
                    onPress={() => choose(dataset)}
                  />
                ))}
              </View>
            ) : (
              <CardNotice text="没有找到对得上的数据，可以上传一个新文件。" />
            )}
            {all ? (
              <AllDatasets
                exclude={payload.datasetIds}
                disabled={disabled}
                onChoose={choose}
              />
            ) : null}
            {all || !open ? null : (
              <SecondaryButton
                title="查看全部"
                icon={List}
                disabled={disabled}
                onPress={() => setAll(true)}
              />
            )}
            <ApdmUploadButton
              appearance="row"
              disabled={disabled}
              onUploaded={async info => {
                if (chat.send('分析这个表格', { attachments: [info] }))
                  setChosen('：上传新文件');
              }}
            />
          </>
        )}
      </ChatCard>
    );
  },
);
